import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiErrorMessage, BACKEND_URL } from "../../lib/api";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { formatBytes, formatDateTime } from "../../lib/format";

const MAX_UPLOAD_MB = 95;   // Cloudflare typically rejects > 100 MB
const WARN_UPLOAD_MB = 50;  // Warn user about slow uploads above this

const PLATFORMS = [
    { key: "mac",       label: "macOS · Universal",      accept: ".dmg,.pkg,.zip", hint: ".dmg · .pkg · .zip · Intel + Apple Silicon" },
    { key: "mac_intel", label: "macOS · Intel",          accept: ".dmg,.pkg,.zip", hint: ".dmg · .pkg · .zip · x86_64 (only if you want separate builds)" },
    { key: "mac_arm64", label: "macOS · Apple Silicon",  accept: ".dmg,.pkg,.zip", hint: ".dmg · .pkg · .zip · arm64 (only if you want separate builds)" },
    { key: "windows",   label: "Windows",                accept: ".exe,.msi,.zip", hint: ".exe · .msi · .zip · 64-bit" },
];

function describeUploadError(err) {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    if (detail) return typeof detail === "string" ? detail : apiErrorMessage(err);
    if (status === 413) return `File too large for the proxy (limit ~${MAX_UPLOAD_MB} MB). Use the "External URL" field with GitHub Releases / S3 / R2.`;
    if (status === 502 || status === 504) return "Server timeout — the file is too large or the connection is slow. Try an external URL.";
    if (status === 401 || status === 403) return "Session expired or insufficient permissions. Log in again.";
    if (status === 500) return "Internal server error while saving the file. Check the logs.";
    if (err?.code === "ERR_NETWORK") return "No connection to the server or the proxy cut off the upload.";
    return apiErrorMessage(err) || "Could not upload the file.";
}

export default function AdminDesktop() {
    const [release, setRelease] = useState(null);
    const [meta, setMeta] = useState({ version: "", release_notes: "" });
    const [ext, setExt] = useState({ mac: "", mac_intel: "", mac_arm64: "", windows: "" });
    const [savingMeta, setSavingMeta] = useState(false);
    const [uploadingPlat, setUploadingPlat] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadSize, setUploadSize] = useState(0);

    const load = async () => {
        try {
            const { data } = await api.get("/admin/desktop/release");
            setRelease(data);
            setMeta({ version: data?.version || "1.0.0", release_notes: data?.release_notes || "" });
            const platforms = data?.platforms || {};
            setExt({
                mac:       platforms.mac?.external_url || "",
                mac_intel: platforms.mac_intel?.external_url || "",
                mac_arm64: platforms.mac_arm64?.external_url || "",
                windows:   platforms.windows?.external_url || "",
            });
        } catch (err) {
            toast.error(apiErrorMessage(err));
        }
    };
    useEffect(() => { load(); }, []);

    const saveMeta = async (e) => {
        e?.preventDefault();
        setSavingMeta(true);
        try {
            await api.patch("/admin/desktop/release", {
                version: meta.version,
                release_notes: meta.release_notes,
                external_url_mac:       ext.mac,
                external_url_mac_intel: ext.mac_intel,
                external_url_mac_arm64: ext.mac_arm64,
                external_url_windows:   ext.windows,
            });
            toast.success("Changes saved");
            await load();
        } catch (err) {
            toast.error(apiErrorMessage(err));
        } finally {
            setSavingMeta(false);
        }
    };

    const upload = async (platform, file) => {
        if (!file) return;

        // ── Pre-flight size checks ───────────────────────────────────────
        const sizeMb = file.size / (1024 * 1024);
        if (sizeMb > MAX_UPLOAD_MB) {
            toast.error(
                `The file is ${sizeMb.toFixed(1)} MB and exceeds the proxy limit of ${MAX_UPLOAD_MB} MB.`,
                { duration: 8000, description: `Upload it to GitHub Releases / S3 / Cloudflare R2 and paste the URL in the "External URL ${platform === "mac" ? "macOS" : "Windows"}" field.` }
            );
            return;
        }
        if (sizeMb > WARN_UPLOAD_MB) {
            const ok = window.confirm(
                `This file is ${sizeMb.toFixed(1)} MB. The upload may take several minutes ` +
                `and depends on the proxy limit (~${MAX_UPLOAD_MB} MB).\n\n` +
                `Recommended for large files: use "External URL" with GitHub Releases.\n\n` +
                `Continue anyway?`
            );
            if (!ok) return;
        }

        const fd = new FormData();
        fd.append("platform", platform);
        fd.append("file", file);
        setUploadingPlat(platform);
        setUploadSize(file.size);
        setUploadProgress(0);

        try {
            await api.post("/admin/desktop/release/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 10 * 60 * 1000, // 10 min
                onUploadProgress: (e) => {
                    if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100));
                },
            });
            toast.success(`Binary for ${platform} uploaded successfully`);
            await load();
        } catch (err) {
            toast.error(describeUploadError(err), { duration: 9000 });
        } finally {
            setUploadingPlat(null);
            setUploadProgress(0);
            setUploadSize(0);
        }
    };

    const removeBinary = async (platform) => {
        if (!window.confirm(`Delete the ${platform} binary?`)) return;
        try {
            await api.delete(`/admin/desktop/release/${platform}`);
            toast.success("Binary deleted");
            await load();
        } catch (err) {
            toast.error(apiErrorMessage(err));
        }
    };

    const platforms = release?.platforms || {};

    return (
        <DashboardLayout role="admin">
            <div className="mb-8" data-testid="admin-desktop-page">
                <div className="label-mono mb-3">/ Desktop client</div>
                <h1 className="font-heading font-black text-3xl lg:text-4xl tracking-tighter">Desktop app</h1>
                <p className="text-avx-muted mt-2 max-w-2xl">
                    Manage the AVX Pro client release. Upload the macOS <span className="text-avx-blue font-mono">.dmg</span> binaries (Intel and Apple Silicon separately) and the Windows <span className="text-avx-blue font-mono">.exe</span>, or reference external URLs (GitHub Releases, S3, R2).
                </p>
            </div>

            <form onSubmit={saveMeta} className="panel p-6 mb-8 space-y-4" data-testid="release-meta-form">
                <div className="label-mono mb-1">/ Release metadata</div>
                <div className="grid md:grid-cols-3 gap-4">
                    <div className="md:col-span-1">
                        <Label className="label-mono">Version</Label>
                        <Input
                            className="mt-2 bg-avx-bg border-avx-line h-11 font-mono"
                            value={meta.version}
                            onChange={(e) => setMeta({ ...meta, version: e.target.value })}
                            placeholder="1.0.0"
                            data-testid="release-version-input"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <Label className="label-mono">Release notes (shown on the public site)</Label>
                        <Textarea
                            rows={3}
                            className="mt-2 bg-avx-bg border-avx-line"
                            value={meta.release_notes}
                            onChange={(e) => setMeta({ ...meta, release_notes: e.target.value })}
                            placeholder="Improvements, fixes, newly supported plugins…"
                            data-testid="release-notes-input"
                        />
                    </div>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {PLATFORMS.map((p) => (
                        <div key={p.key}>
                            <Label className="label-mono">External URL {p.label} (optional)</Label>
                            <Input
                                className="mt-2 bg-avx-bg border-avx-line h-11 font-mono text-xs"
                                value={ext[p.key]}
                                onChange={(e) => setExt({ ...ext, [p.key]: e.target.value })}
                                placeholder="https://…"
                                data-testid={`release-external-${p.key}`}
                            />
                        </div>
                    ))}
                </div>
                <div className="flex items-center gap-3">
                    <button
                        type="submit"
                        disabled={savingMeta}
                        className="btn-primary px-6 py-2.5 text-xs font-mono uppercase tracking-[0.2em] rounded-sm"
                        data-testid="release-save-btn"
                    >
                        {savingMeta ? "Saving…" : "Save changes"}
                    </button>
                    {release?.updated_at && (
                        <span className="text-xs font-mono text-avx-muted">
                            Last updated: {formatDateTime(release.updated_at)}
                        </span>
                    )}
                </div>
            </form>

            <div className="panel p-4 mb-6 bg-avx-blue/5 border-avx-blue/30" data-testid="upload-limit-notice">
                <div className="flex items-start gap-3">
                    <div className="text-avx-blue text-lg leading-none mt-0.5">ℹ</div>
                    <div className="text-xs font-mono text-avx-text/90 leading-relaxed">
                        <span className="text-avx-blue font-bold">Direct upload limit: ~{MAX_UPLOAD_MB} MB.</span>
                        {" "}For larger binaries (a typical Electron .dmg is 150-250 MB),
                        upload the file to <span className="text-avx-blue">GitHub Releases / S3 / Cloudflare R2</span> and
                        paste the public URL in the <span className="text-avx-blue">"External URL"</span> field of each platform above.
                    </div>
                </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
                {PLATFORMS.map((p) => {
                    const info = platforms[p.key];
                    const hasBinary = !!info?.storage_path;
                    const hasExternal = !!info?.external_url;
                    const isAvailable = hasBinary || hasExternal;
                    const isUploading = uploadingPlat === p.key;
                    const sizeMbStr = uploadSize ? (uploadSize / (1024 * 1024)).toFixed(1) : "0";
                    const badgeLabel = hasBinary
                        ? "Available · binary"
                        : hasExternal
                            ? "Available · external URL"
                            : "No binary";
                    return (
                        <div key={p.key} className="panel p-6 flex flex-col" data-testid={`platform-card-${p.key}`}>
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="font-heading font-bold text-xl">{p.label}</h2>
                                <span
                                    className={`text-[10px] font-mono uppercase tracking-[0.18em] px-2 py-1 rounded-sm ${isAvailable ? "bg-avx-success/15 text-avx-success" : "bg-avx-line text-avx-muted"}`}
                                    data-testid={`platform-status-${p.key}`}
                                >
                                    {badgeLabel}
                                </span>
                            </div>
                            <div className="label-mono text-[10px] mb-1">{p.hint}</div>
                            <div className="label-mono text-[10px] mb-4 text-avx-muted">Max {MAX_UPLOAD_MB} MB · larger files → external URL</div>

                            {hasBinary && (
                                <div className="bg-avx-bg/60 border border-avx-line rounded-sm p-3 mb-4 text-xs font-mono space-y-1">
                                    <div className="truncate" title={info.file_name}>{info.file_name}</div>
                                    <div className="text-avx-muted">{formatBytes(info.file_size)} · uploaded {formatDateTime(info.uploaded_at)}</div>
                                    <a
                                        href={`${BACKEND_URL}/api/desktop/files/${p.key}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-avx-blue hover:underline text-[10px] uppercase tracking-[0.18em] inline-block mt-1"
                                        data-testid={`platform-test-download-${p.key}`}
                                    >
                                        Test download →
                                    </a>
                                </div>
                            )}

                            {hasExternal && (
                                <div className="bg-avx-bg/60 border border-avx-line rounded-sm p-3 mb-4 text-xs font-mono space-y-1">
                                    <div className="label-mono mb-1">
                                        External URL {hasBinary && <span className="text-avx-muted normal-case tracking-normal">(fallback)</span>}
                                    </div>
                                    <a
                                        href={info.external_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-avx-blue hover:underline break-all"
                                        data-testid={`platform-external-link-${p.key}`}
                                    >
                                        {info.external_url}
                                    </a>
                                    <a
                                        href={info.external_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-avx-blue hover:underline text-[10px] uppercase tracking-[0.18em] inline-block mt-1"
                                        data-testid={`platform-external-test-${p.key}`}
                                    >
                                        Test external download →
                                    </a>
                                </div>
                            )}

                            {isUploading && (
                                <div className="mb-3" data-testid={`upload-progress-${p.key}`}>
                                    <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.18em] mb-1.5">
                                        <span className="text-avx-blue">Uploading · {sizeMbStr} MB</span>
                                        <span className="text-avx-blue">{uploadProgress}%</span>
                                    </div>
                                    <div className="w-full h-1.5 bg-avx-line rounded-sm overflow-hidden">
                                        <div
                                            className="h-full bg-avx-blue transition-all duration-200"
                                            style={{ width: `${uploadProgress}%` }}
                                        />
                                    </div>
                                </div>
                            )}

                            <label className={isUploading ? "cursor-not-allowed" : "cursor-pointer"}>
                                <input
                                    type="file"
                                    className="hidden"
                                    accept={p.accept}
                                    disabled={isUploading}
                                    onChange={(e) => upload(p.key, e.target.files?.[0])}
                                    data-testid={`platform-upload-${p.key}`}
                                />
                                <div className={`btn-${hasBinary ? "secondary" : "primary"} px-4 py-2.5 text-xs font-mono uppercase tracking-[0.2em] rounded-sm text-center ${isUploading ? "opacity-60" : ""}`}>
                                    {isUploading
                                        ? `Uploading ${uploadProgress}%…`
                                        : hasBinary ? "Replace binary" : "Upload binary"}
                                </div>
                            </label>

                            {hasBinary && (
                                <button
                                    onClick={() => removeBinary(p.key)}
                                    className="text-[10px] font-mono uppercase tracking-[0.18em] text-avx-danger hover:underline mt-3 self-start"
                                    data-testid={`platform-remove-${p.key}`}
                                >
                                    Delete binary
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>

            <div className="panel p-6 mt-8">
                <div className="label-mono mb-3">/ How to build the app</div>
                <div className="text-sm text-avx-text/90 font-mono leading-relaxed space-y-2">
                    <p>The app source code lives in <span className="text-avx-blue">/app/desktop</span>. To package it:</p>
                    <pre className="bg-avx-bg/60 border border-avx-line rounded-sm p-3 text-xs overflow-x-auto">{`# macOS (.dmg)
cd /app/desktop && yarn install && yarn build:mac

# Windows (.exe)
yarn build:win

# Upload the resulting binary (dist/*.dmg | dist/*.exe) from this panel.`}</pre>
                    <p className="text-avx-muted text-xs">
                        Tip: the public URL is <span className="text-avx-blue">{BACKEND_URL}/desktop</span> — as soon as you upload a binary it will be available to all visitors.
                    </p>
                </div>
            </div>
        </DashboardLayout>
    );
}
